// src/context/ProfileContext.js

import React, { createContext, useState, useContext, useEffect, useCallback, useRef, useMemo } from "react";
import { handleGoogleRedirectResult, fetchUserProfile } from '../services/auth';
import { getAuth, onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { auth } from "../services/auth";
import { getFirestore, doc, getDoc, updateDoc, Timestamp, onSnapshot } from "firebase/firestore";
import fetchLinkedArtistAccounts from "../services/fetchLinkedArtistAccounts";
import { useSharedAnimation } from "../context/AnimationContext";
import toast from 'react-hot-toast';
import LegalMigrationModal from "../components/miscellaneous/LegalMigrationModal";

const TIER_CONFIG = {
  freemium: { dailyCap: 100, pointsPerRating: 3 },
  tier1: { dailyCap: 1000, pointsPerRating: 15 },
  tier2: { dailyCap: Infinity, pointsPerRating: 15 },
};

// --- Singleton Initializer ---
// This logic now lives OUTSIDE the React component and is guaranteed to run only once.
let authInitializationPromise = null;

// ✅ ADD THIS HELPER FUNCTION

const db = getFirestore();

/**
 * Checks if a user's streak is broken and resets it to 0 if needed.
 * @param {string} userId - The ID of the user.
 * @param {object} userProfile - The user's profile data from Firestore.
 */
const checkAndResetStreak = async (userId, userProfile) => {
  if (!userId || !userProfile) return;

  const { lastRatedTimestamp, dailyStreak } = userProfile;

  // A streak is only considered broken if it's currently > 0
  if (!dailyStreak || dailyStreak === 0) {
    return; 
  }

  // Check if last rating was more than 48 hours ago
  const now = Timestamp.now();
  const fortyEightHoursInSeconds = 48 * 60 * 60;
  
  let isBroken = true; // Assume broken if there's no timestamp
  if (lastRatedTimestamp) {
    isBroken = (now.seconds - lastRatedTimestamp.seconds) > fortyEightHoursInSeconds;
  }

  if (isBroken) {

    try {
      const db = getFirestore();
      const userRef = doc(db, "users", userId);
      // Update the database so the UI listener in TopMenuBar will see the change
      await updateDoc(userRef, { dailyStreak: 0 });
    } catch (error) {

    }
  }
};

// --- THIS IS THE NEW, REUSABLE FUNCTION ---
const refreshApp = async (currentUser, setters) => {
  if (!currentUser) return;

  const { setUserProfile, updateLinkedArtistAccounts, switchProfile, setActiveProfile } = setters;

  try {
    // This logic is copied from your onAuthStateChanged listener
    let initialProfile = await fetchUserProfile(currentUser.uid);
    await checkAndResetStreak(currentUser.uid, initialProfile);

    const finalProfileData = await fetchUserProfile(currentUser.uid);
    // 1. Fetch all linked accounts
    const allArtistAccounts = await fetchLinkedArtistAccounts(currentUser.uid);

    // ✅ 2. FILTER out soft-deleted accounts here
    const activeArtistAccounts = allArtistAccounts.filter(acc => acc.isDeleted !== true);

    setUserProfile(finalProfileData);
    updateLinkedArtistAccounts(activeArtistAccounts.map(a => ({...a, type: 'artist'})));

    if (finalProfileData) {
      // Logic to determine which profile was active and restore it
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      const normalizedUserProfile = { ...finalProfileData, type: "user", id: currentUser.uid };
      let profileToSet = normalizedUserProfile;
      if (stored) {
        const parsed = JSON.parse(stored);
        const allActiveProfiles = [normalizedUserProfile, ...activeArtistAccounts.map(a => ({...a, type: 'artist'}))];
        const found = allActiveProfiles.find(p => p.id === parsed.id && p.type === parsed.type);
        if (found) profileToSet = found;
      }
      switchProfile(profileToSet);
    } else {
      switchProfile({ id: currentUser.uid, email: currentUser.email, type: 'user' });
    }
  } catch (err) {
    console.error("PROFILE_CONTEXT: Error during data refresh.", err);
    // Fallback to a basic profile if refresh fails
    setActiveProfile({ id: currentUser.uid, email: currentUser.email, type: 'user' });
  }
};

const initializeAuth = (setters) => {
  // If initialization has already started, return the existing promise.
  if (authInitializationPromise) {
    return authInitializationPromise;
  }
  
  // Start initialization only once.
  authInitializationPromise = new Promise((resolve) => {
    const { 
        setUser, 
        setUserProfile, 
        setActiveProfile, 
        setIsLoading, 
        switchProfile, 
        updateLinkedArtistAccounts,
        triggerStreakPop, 
        streakRef,
        isInitialProfileLoad
    } = setters;

    //const auth = getAuth();
    
    // Set up the persistent listener. It's the ultimate source of truth.
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
        
        if (currentUser) {
            setUser(currentUser); // Set the raw Firebase user object

            // --- Set up the main real-time listener for the user's profile ---
            const db = getFirestore();
            const userRef = doc(db, "users", currentUser.uid);

            const userListener = onSnapshot(userRef, async (docSnap) => {
                if (docSnap.exists()) {
                    const profileData = docSnap.data();
                    setUserProfile(profileData); // 1. Update the main profile state


                    // 2. Fetch linked artist accounts
                    const artistAccounts = await fetchLinkedArtistAccounts(currentUser.uid);
                    updateLinkedArtistAccounts(artistAccounts.map(a => ({ ...a, type: 'artist' })));
                    
                    // 3. Determine and set the active profile (restoring from localStorage)
                    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
                    const normalizedUserProfile = { ...profileData, type: "user", id: currentUser.uid };
                    let profileToSet = normalizedUserProfile;
                    if (stored) {
                        const parsed = JSON.parse(stored);
                        const allProfiles = [normalizedUserProfile, ...artistAccounts.map(a => ({ ...a, type: 'artist' }))];
                        const found = allProfiles.find(p => p.id === parsed.id && p.type === parsed.type);
                        if (found) profileToSet = found;
                    }
                    switchProfile(profileToSet); // switchProfile also sets activeProfile

                } else {
                    // This case handles a user who is in Firebase Auth but doesn't have a Firestore document yet.
                    switchProfile({ id: currentUser.uid, email: currentUser.email, type: 'user' });
                }
            });
            
            // You would ideally store `userListener` to unsubscribe on logout.

        } else {
            // --- User is logged out, sign them in as a guest ---
            try {
                await signInAnonymously(auth);
            } catch (error) {
                console.error("PROFILE_CONTEXT: Anonymous sign-in failed.", error);
                // If anonymous sign-in fails, clear all profile state.
                setActiveProfile(null);
                setUserProfile(null);
                updateLinkedArtistAccounts([]);
                localStorage.removeItem(LOCAL_STORAGE_KEY);
            }
        }
        
        setIsLoading(false);
        resolve(unsubscribe); // Resolve the promise for the main auth listener
    });

    handleGoogleRedirectResult()
      .then((data) => {
        if (data) {
          
        } else {
        
        }
      })
      .catch(error => {
        console.error("PROFILE_CONTEXT RESULT: Global error processing sign-in redirect:", error);
      });
  });

  return authInitializationPromise;
};


// --- React Provider Component ---

const ProfileContext = createContext();
export const useProfile = () => useContext(ProfileContext);

const LOCAL_STORAGE_KEY = "activeProfile";

export const ProfileProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); 
  const [activeProfile, setActiveProfile] = useState(null);
  const [linkedArtistAccounts, setLinkedArtistAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { triggerStreakPop } = useSharedAnimation(); // 👈 Get the animation trigger
  const streakRef = useRef(0);
  const isInitialProfileLoad = useRef(true);
  const [unreadPostCount, setUnreadPostCount] = useState(0);
  const [feedReturnPath, setFeedReturnPath] = useState('/');
  const [settingsReturnPath, setSettingsReturnPath] = useState('/');
  const [isDarkMode, setIsDarkMode] = useState(() => {
      // Check localStorage first (if you save non-logged-in settings there)
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) return savedTheme === 'dark';
      
      // Fallback: Default to true (Dark Mode) for everyone else
      return true; 
  });
  const isPremium = userProfile?.premiumSubscription?.status === 'active';
  const [isAdmin, setIsAdmin] = useState(false);

  const pointEligibility = useMemo(() => {
    if (!userProfile) {
      return { canEarn: false, pointsToAward: 0, dailyCap: 100, pointsToday: 0 };
    }

    const tier = userProfile?.subscriptionTier || userProfile?.premiumSubscription?.tier || "freemium";
    const config = TIER_CONFIG[tier] || TIER_CONFIG.freemium;

    const todayStr = new Date().toISOString().split("T")[0];
    const lastReset = userProfile?.lastPointsResetDate || "";
    const pointsToday = lastReset === todayStr ? (userProfile?.dailyPointsEarned || 0) : 0;

    const canEarn = pointsToday < config.dailyCap;

    return {
      canEarn,
      pointsToAward: canEarn ? config.pointsPerRating : 0,
      dailyCap: config.dailyCap,
      pointsToday,
    };
  }, [userProfile]);


  const incrementDailyPointsOptimistic = useCallback((pointsEarned) => {
    if (!pointsEarned || pointsEarned <= 0) return;

    const todayStr = new Date().toISOString().split("T")[0];

    setUserProfile((prev) => {
      if (!prev) return prev;

      const lastReset = prev.lastPointsResetDate || "";
      const currentEarned = lastReset === todayStr ? (prev.dailyPointsEarned || 0) : 0;

      return {
        ...prev,
        dailyPointsEarned: currentEarned + pointsEarned,
        lastPointsResetDate: todayStr,
        points: (prev.points || 0) + pointsEarned,
      };
    });
  }, []);

   // ✅ 2. Define functions with NO dependencies first.
  const updateLinkedArtistAccounts = useCallback((accounts) => {
    if (!Array.isArray(accounts)) return;
    setLinkedArtistAccounts(accounts);
  }, []);

  const switchProfile = useCallback((profile) => {
    if (!profile) return;
    
    
    setActiveProfile(profile);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(profile));
  }, []);

  // ✅ 3. Define functions that HAVE dependencies last.
  const refreshContext = useCallback(async () => {
    //const auth = getAuth();
    if (auth.currentUser) {
      
      const setters = { setUserProfile, updateLinkedArtistAccounts, switchProfile, setActiveProfile };
      await refreshApp(auth.currentUser, setters);
    }
  }, [switchProfile, updateLinkedArtistAccounts, setActiveProfile, setUserProfile]); // Now its dependencies are declared above it.

  // 4. Your useEffect hook can now safely run once on mount.
  useEffect(() => {

    let unsubscribe;
    const setters = { 
        setUser, 
        setUserProfile, 
        setActiveProfile, 
        setIsLoading, 
        switchProfile, 
        updateLinkedArtistAccounts,
        triggerStreakPop,
        streakRef,
        isInitialProfileLoad
    };
    initializeAuth(setters).then(unsub => { unsubscribe = unsub; });
    return () => { if (unsubscribe) { unsubscribe(); } };
  }, []); // Use an empty array to run only once.

  useEffect(() => {
      if (user?.uid) {
          const userRef = doc(db, 'users', user.uid);
          // Listen for real-time changes to the user document
          const unsubscribe = onSnapshot(userRef, (docSnap) => {
              if (docSnap.exists()) {
                  const data = docSnap.data();
                  setUnreadPostCount(docSnap.data().unreadPostCount || 0);
                  setIsDarkMode(!!data.darkMode);
              }
          });
          return () => unsubscribe();
      }
  }, [user]);

  const toggleDarkMode = useCallback(async () => {
      if (!user?.uid || user.isAnonymous) {
          // For guests, just toggle locally
          setIsDarkMode(prev => !prev);
          return;
      }

      try {
          const userRef = doc(db, "users", user.uid);
          await updateDoc(userRef, { darkMode: !isDarkMode });
          // The onSnapshot above will handle setting the local state
      } catch (error) {
          console.error("Error updating theme:", error);
          toast.error("Failed to save theme preference.");
      }
  }, [user, isDarkMode]);

  useEffect(() => {
      if (user) {
          user.getIdTokenResult(true).then((idTokenResult) => {

              setIsAdmin(!!idTokenResult.claims.admin);
          });
      } else {
          setIsAdmin(false);
      }
  }, [user]);

  const value = useMemo(() => ({
    user,
    userProfile,
    activeProfile,
    switchProfile,
    linkedArtistAccounts,
    updateLinkedArtistAccounts,
    isLoading,
    isAdmin,
    refreshContext,
    unreadPostCount,
    feedReturnPath,
    setFeedReturnPath,
    settingsReturnPath,
    setSettingsReturnPath,
    isDarkMode,
    toggleDarkMode,
    isPremium,
    canEarnPoints: pointEligibility.canEarn,
    pointsToAward: pointEligibility.pointsToAward,
    dailyCap: pointEligibility.dailyCap,
    pointsToday: pointEligibility.pointsToday,
    incrementDailyPointsOptimistic,
  }), [
    user,
    userProfile,
    activeProfile,
    switchProfile,
    linkedArtistAccounts,
    updateLinkedArtistAccounts,
    isLoading,
    isAdmin,
    refreshContext,
    unreadPostCount,
    feedReturnPath,
    setFeedReturnPath,
    settingsReturnPath,
    setSettingsReturnPath,
    isDarkMode,
    toggleDarkMode,
    isPremium,
    pointEligibility,
    incrementDailyPointsOptimistic,
  ]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};